<template>
  <div>
    <overlay :value="loading"></overlay>
    <v-card class="pa-6">
      <v-card-title class="text-uppercase">
        <v-row class="my-1 justify-space-between align-center">
          <div>{{ this.service === null ? 'Crear nuevo' : 'Editar' }} servicio</div>
        </v-row>
      </v-card-title>
      <hr class="my-3" />
      <v-card-text class="my-0 py-0">
        <v-form ref="newServiceForm" v-model="formValid">
          <v-container class="ma-0 pa-0">
            <v-col class="ma-0 pa-0">
              <v-row class="mt-1 mb-n5">
                <v-col cols="4">
                  <v-text-field label="Ref" v-model="newService.ref" outlined dense></v-text-field>
                </v-col>
                <v-col cols="8">
                  <v-text-field
                    label="Título"
                    v-model="newService.title"
                    required
                    outlined
                    dense
                    :rules="titleRules"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="my-1">
                <v-col cols="6">
                  <v-select
                    label="Ícono"
                    v-model="newService.iconId"
                    :items="iconIds"
                    clearable
                    outlined
                    dense
                    hide-details="auto"
                  >
                    <template v-slot:selection="{ item }">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-img v-bind="attrs" v-on="on" :src="iconAssetPath(item)" max-height="20" max-width="20" contain></v-img>
                        </template>
                        <span>{{ item }}</span>
                      </v-tooltip>
                    </template>
                    <template v-slot:item="{ item, attrs, on }">
                      <v-list-item v-bind="attrs" v-on="on">
                        <v-tooltip top>
                          <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                            <v-img
                              v-bind="tooltipAttrs"
                              v-on="tooltipOn"
                              :src="iconAssetPath(item)"
                              max-height="24"
                              max-width="24"
                              contain
                            ></v-img>
                          </template>
                          <span>{{ item }}</span>
                        </v-tooltip>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    label="Orden"
                    v-model.number="newService.order"
                    required
                    outlined
                    dense
                    type="number"
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="3" class="d-flex align-center">
                  <v-switch
                    class="mt-0 pt-0"
                    label="Activo"
                    v-model="newService.active"
                    hide-details="auto"
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row class="my-1">
                <v-col cols="8">
                  <v-select
                    label="Tipo de servicio"
                    v-model="newService.id_ST"
                    :items="serviceTypes"
                    :loading="loadingServiceTypes"
                    item-text="name"
                    item-value="id"
                    clearable
                    outlined
                    dense
                    hide-details="auto"
                  ></v-select>
                </v-col>
                <v-col cols="4" class="d-flex align-center">
                  <v-btn text color="primary" @click="openCreateTypeDialog">
                    <v-icon left small>mdi-plus</v-icon>Crear tipo nuevo
                  </v-btn>
                </v-col>
              </v-row>

              <v-row class="my-1" v-if="newService.id_ST">
                <v-col cols="12">
                  <div class="text-caption grey--text mb-1">Documentos requeridos por este tipo</div>
                  <template v-if="selectedTypeAttachments.length">
                    <v-chip v-for="attachment in selectedTypeAttachments" :key="attachment.id" small class="mr-1 mb-1">
                      {{ attachment.name }}
                    </v-chip>
                  </template>
                  <span v-else class="text-caption grey--text">Sin documentos requeridos</span>
                </v-col>
              </v-row>

              <v-row class="my-1">
                <v-col cols="12">
                  <v-textarea
                    label="De qué trata el servicio (igual en todos los países)"
                    v-model="newService.whatIs"
                    outlined
                    auto-grow
                    rows="2"
                    dense
                    hide-details="auto"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-col>
          </v-container>
        </v-form>
      </v-card-text>
      <hr class="my-3" />
      <v-card-actions class="justify-end">
        <v-btn text color="primary" rounded @click="closeDialog">Cerrar</v-btn>
        <v-btn :disabled="!formValid" color="primary" rounded @click="validate">
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar v-model="error" :timeout="5000">
      {{ errorMessage }}

      <template v-slot:action="{ attrs }">
        <v-btn color="red" text v-bind="attrs" @click="error = false"> Entendido </v-btn>
      </template>
    </v-snackbar>

    <!-- Diálogo anidado: crear un Tipo de servicio sin salir del formulario del servicio -->
    <v-dialog v-model="showTypeDialog" min-width="600" max-width="900" @keydown.esc="closeCreateTypeDialog">
      <template>
        <service-type-maintainer
          :service-type="null"
          @update-list="onTypeCreated"
          @close-dialog="closeCreateTypeDialog"
        ></service-type-maintainer>
      </template>
    </v-dialog>
  </div>
</template>

<script>
import Overlay from '../components/Overlay.vue';
import ServiceTypeMaintainer from '../components/ServiceTypeMaintainer.vue';
import ServicesService from '../services/services.service';
import CountryService from '../services/countries.service';
import { SERVICE_ICON_IDS } from '../utils/constants';

export default {
  components: { Overlay, ServiceTypeMaintainer },
  name: 'ServiceMaintainer',
  props: ['service'],
  data() {
    return {
      loading: false,
      error: false,
      errorMessage: 'Ocurrió un error guardando el servicio, intenta más tarde',
      newService: this.emptyService(),
      serviceTypes: [],
      loadingServiceTypes: false,
      showTypeDialog: false,
      formValid: false,
      titleRules: [v => !!v || 'El campo es requerido'],
      iconIds: SERVICE_ICON_IDS
    };
  },
  computed: {
    selectedTypeAttachments() {
      const selectedType = this.serviceTypes.find(type => type.id === this.newService.id_ST);
      return selectedType && selectedType.requiredAttachments ? selectedType.requiredAttachments : [];
    }
  },
  watch: {
    service(val) {
      this.newService = this.emptyService();
      this.$refs.newServiceForm.resetValidation();
      this.parseService(val !== null);
    }
  },
  mounted() {
    this.newService = this.emptyService();
    this.fetchServiceTypes();
    this.parseService(this.service !== null);
  },
  methods: {
    // Íconos servidos como estáticos en public/icons/services/<iconId>.svg
    // (copiados de pawer_ui_app/assets/icons/icon-services/<iconId>/<iconId>_primary_100.svg).
    iconAssetPath(iconId) {
      return `${process.env.BASE_URL}icons/services/${iconId}.svg`;
    },
    // Todas las claves deben declararse aquí (aunque sea en null/default) para
    // que Vue 2 las trate como reactivas desde el inicio. Si se agregan recién
    // en parseService() sobre un objeto ya observado (ex-{}), Vue nunca
    // detecta esas altas de propiedad — el `computed` selectedTypeAttachments
    // (y cualquier binding que dependa de id_ST) deja de reaccionar a sus
    // cambios (bug: no actualizaba los documentos requeridos al cambiar el
    // tipo de servicio).
    emptyService() {
      return {
        id: null,
        ref: '',
        title: '',
        iconId: null,
        order: 0,
        active: true,
        id_ST: null,
        whatIs: ''
      };
    },
    closeDialog() {
      this.$emit('close-dialog');
    },
    async fetchServiceTypes() {
      this.loadingServiceTypes = true;
      const res = await ServicesService.getServiceTypes();
      if (res.status === 'ok') this.serviceTypes = res.data;
      this.loadingServiceTypes = false;
    },
    openCreateTypeDialog() {
      this.showTypeDialog = true;
    },
    closeCreateTypeDialog() {
      this.showTypeDialog = false;
    },
    async onTypeCreated(createdType) {
      await this.fetchServiceTypes();
      if (createdType && createdType.id) {
        this.newService.id_ST = createdType.id;
      }
      this.closeCreateTypeDialog();
    },
    parseService(exist) {
      this.newService.id = exist ? this.service.id : null;
      this.newService.ref = exist ? this.service.ref || '' : '';
      this.newService.title = exist ? this.service.title : '';
      this.newService.iconId = exist ? this.service.iconId || '' : '';
      this.newService.order = exist ? this.service.order ?? 0 : 0;
      this.newService.active = exist ? this.service.active : true;
      this.newService.id_ST = exist
        ? (this.service.serviceType ? this.service.serviceType.id : this.service.id_ST) || null
        : null;
      this.newService.whatIs = exist ? this.service.whatIs || '' : '';
      this.$nextTick(() => {
        if (this.$refs.newServiceForm) this.$refs.newServiceForm.validate();
      });
    },
    validate() {
      if (this.$refs.newServiceForm.validate()) {
        if (this.service === null) {
          this.createService();
        } else {
          this.updateService();
        }
      }
    },
    buildPayload() {
      return {
        ref: this.newService.ref || null,
        title: this.newService.title,
        iconId: this.newService.iconId || null,
        order: this.newService.order || 0,
        active: this.newService.active,
        id_ST: this.newService.id_ST || null,
        whatIs: this.newService.whatIs || null
      };
    },
    async createService() {
      this.loading = true;
      const res = await ServicesService.createService(this.buildPayload());
      if (res.status === 'ok') {
        const createdId = res.data && res.data.id;
        if (createdId) await this.createEmptyServicePlanDefaults(createdId);
        this.loading = false;
        this.$refs.newServiceForm.resetValidation();
        this.$emit('update-list');
        this.closeDialog();
      } else {
        this.loading = false;
        this.errorMessage = 'Ocurrió un error guardando el servicio, intenta más tarde';
        this.error = true;
      }
    },
    // FR-011/US5: al crear un servicio se dejan listos (vacíos) sus defaults por
    // país, para que quien los complete después no tenga que "crearlos" desde cero.
    async createEmptyServicePlanDefaults(serviceId) {
      const countriesRes = await CountryService.getAllCountries(true);
      const countries = countriesRes && countriesRes.status === 'ok' ? countriesRes.data : [];
      if (!countries.length) return;
      const results = await Promise.all(
        countries.map(country =>
          ServicesService.createServicePlanDefault({
            serviceId,
            countryId: country.id,
            maxAnnualAmount: 0,
            maxAnnualEvents: 0,
            graceDays: 0,
            refundReviewDays: 0,
            maxAmountPerEvent: 0
          })
        )
      );
      if (results.some(r => r.status !== 'ok')) {
        this.errorMessage = 'Servicio creado, pero no se pudieron generar todos los defaults por país';
        this.error = true;
      }
    },
    async updateService() {
      this.loading = true;
      const res = await ServicesService.updateService(this.newService.id, this.buildPayload());
      this.loading = false;
      if (res.status === 'ok') {
        this.$refs.newServiceForm.resetValidation();
        this.$emit('update-list');
        this.closeDialog();
      } else {
        this.errorMessage = 'Ocurrió un error guardando el servicio, intenta más tarde';
        this.error = true;
      }
    }
  }
};
</script>
